/**
 * draft.js — a classified case folder → a valid ReportDoc.
 *
 * This is the module that makes the Builder a tool rather than a template. The
 * team's reports vary case to case by most of their content, and the reason is
 * visible the moment you list four real folders:
 *
 *   Case 1  post-plan skull + mandible, splints, teeth   — NO native anything
 *   Case 2  native AND post-plan skull + mandible        — the complete one
 *   Case 3  teeth + one movement splint                  — NO skull at all
 *   Case 4  native skull ×2 (CLEAN.obj and RAW.stl)      — NO post-plan anything
 *
 * So a fixed page list is wrong for three cases out of four. Instead every page
 * declares what it NEEDS; a page whose parts are absent is not built, and the
 * reason is reported. The operator sees "Comparison page not built — no
 * post-plan skull", which is a fact about their case, instead of a page of empty
 * boxes they have to notice.
 *
 * ── The rule that matters most ───────────────────────────────────────────────
 * When two meshes claim the same (role, stage) — Case 4's CLEAN.obj and RAW.stl
 * both classify as skull/native — this module records a CONFLICT and resolves
 * nothing. It does not `.find()` the first one. A stage that silently picks the
 * wrong input and returns a confident, plausible result is the single most
 * expensive failure mode in this codebase, and "the array happened to be in that
 * order" is not a clinical justification. The Builder asks; the operator answers
 * through `picks`.
 */

import {
  newReport, newScene, newScenePart, newMeshRef,
  newTextPage, newInfoPage, newGalleryPage, newCephPage, makeIdFactory, hasCephAnalysis, waiverBlockFor,
  GRID_VIEWS, sixViewGridPage, comparisonGridPage,
} from './schema.js'
import { classify, ROLE_LABELS, stageForRole } from './classify.js'
import { CT_FIELDS } from '../sseReport.js'
import { isPageOrganised, draftFromPages } from './pageDraft.js'
import { ORBITAL_RECIPES } from './orbitalRecipes.js'
import { formatFor, recipesKeyFor, paletteFor } from './formats.js'
import { formatPageTypes } from './formatPages.js'
import { FIBULA_RECIPES } from './fibulaRecipes.js'
import { GENERIC_PAGES, galleryPaging, RENAMED_PAGE_TYPES } from './genericPages.js'
import { partAppearance, isNonAnatomy } from './palette.js'
import {
  AGREEMENT_BODY, TERMS_BODY_1, TERMS_BODY_2, AGREEMENT_STYLES, TERMS_STYLES,
} from './boilerplate.js'

/**
 * The intraoral-scan rows of the Source data page (reference p2). Small and
 * local because nothing else records them yet; CT rows are reused from the
 * shipped report rather than duplicated.
 */
const IOS_FIELDS = [
  ['Scan date', 'scan_date'],
  ['Upper jaw scan', 'upper_jaw'],
  ['Lower jaw scan', 'lower_jaw'],
  ['Bite scan', 'bite'],
  ['In occlusion', 'in_occlusion'],
]

// GRID_VIEWS lives in schema.js now (single-sourced); re-exported for existing importers.
export { GRID_VIEWS } from './schema.js'

/**
 * Build a library from filenames. `origin` tells the renderer where to fetch the
 * bytes later ('upload' | 'drive'); classification is derived here so the UI and
 * the draft can never disagree about what a file is.
 */
/**
 * @param {object} [opts.roleOverrides]  meshId → role, the operator's correction
 * @param {object} [opts.sideOverrides]  meshId → 'left'|'right'|null, likewise.
 *        An explicit `null` CLEARS a wrong derivation and is honoured as such.
 *
 * WHY AN OVERRIDE EXISTS. The classifier reads the filename, and the team's
 * page-numbered exports frequently carry no descriptor at all — `Page 13.OBJ`,
 * `Page 17.OBJ`. Those fall to role 'other' and take the bone tone, where the
 * delivered report shows a white printed appliance.
 *
 * There is no signal in the file to recover it from. The obvious candidate was
 * the polypaint, and it was MEASURED and rejected: `Page 3 - Native skull.OBJ`
 * is 100% white across 1,333,636 vertices — exactly as blank as the splint —
 * because all-white polypaint is ZBrush's default for NOT PAINTED, not a colour
 * choice (see objPolypaint.js hasMeaningfulPaint). Keying on it would render the
 * native skull grey. An earlier attempt keyed on a guide-ish filename and fired
 * on 0 of the corpus's 30 meshes. So the correction has to come from the
 * operator, and this is where it enters.
 *
 * The STAGE follows only where the role settles it — see ROLE_IMPLIED_STAGE.
 * Overriding the role alone was tried and is not enough: measured on a template
 * case, `Page 13.OBJ` set to splint gave `splint/native`, every recipe asking for
 * `splint/output` still missed it, and the correction changed the colour and
 * nothing else. Anatomy is deliberately excluded — a skull is native or post-plan
 * according to the file, never according to being a skull.
 */
/**
 * @param {object} [opts.speOverrides]  meshId → {role, stage, as}, from matching the file's CONTENT
 * against the planning engine's report (speGeometry.js). Sits between the operator and the
 * classifier: the operator's word still wins — they are looking at the case — but a content match
 * beats any filename read, because it says what the mesh IS rather than what it is called. A file
 * with an spe override is additionally marked `speVerified: <plan label>`; that marker states the
 * IDENTITY was proven and survives even an operator role correction, which changes only how the
 * mesh is rendered, not which bytes they are.
 */
export function buildLibrary(files, {
  origin = 'upload', roleOverrides = {}, speOverrides = {}, sideOverrides = {},
} = {}) {
  const seen = new Map()
  return files.map((f) => {
    const name = typeof f === 'string' ? f : f.name
    // The id is content-derived (see meshId), so an override keyed by it means the
    // same mesh for as long as that mesh is present — the guarantee `picks` relies on.
    const id = meshId(name, seen)
    const classified = classify(name)
    const spe = speOverrides[id]
    const override = roleOverrides[id]
    const role = override ?? spe?.role ?? classified.role
    const stage = override ? (stageForRole(role) ?? classified.stage)
      : spe ? (spe.stage ?? classified.stage)
      : classified.stage
    // Laterality: derived from the filename, and the operator's entry wins.
    // Keyed the same way as a role override so a pick means the same mesh for
    // as long as that mesh is present. `null` in the map is a MEANINGFUL entry —
    // the operator clearing a wrong derivation — so presence is tested, not
    // truthiness, or clearing it would silently fall back to the guess.
    const side = Object.prototype.hasOwnProperty.call(sideOverrides, id)
      ? sideOverrides[id] : classified.side
    const ref = newMeshRef(id, name, origin, { ...classified, role, stage, side })
    if (spe?.as) ref.speVerified = spe.as
    // Carry the caller's handle (File object, Drive fileId) untouched. The
    // schema does not model it because it is not part of the saved document.
    return typeof f === 'string' ? ref : { ...ref, handle: f.handle ?? f }
  })
}

/**
 * A mesh id derived from the FILENAME, not from its position in the array.
 *
 * This used to be a counter — m001, m002, … — regenerated on every rebuild of
 * the library. The Builder rebuilds on every keystroke and every file change
 * while the operator's `picks` persist, so an id was a promise the library did
 * not keep:
 *
 *   the operator picks CLEAN.obj (m001); the folder is re-picked and the OS
 *   returns the two skulls in the other order; m001 is now RAW.stl. The panel
 *   still reads "resolved" and the report is built from the mesh they rejected.
 *
 * Silently, on a surgical document — the precise failure this engine's
 * refuse-to-guess design exists to prevent, reintroduced by the UI around it.
 * Content-derived ids make a pick mean the same mesh for as long as that mesh is
 * present, and mean NOTHING once it is gone, which re-raises the conflict
 * instead of resolving it wrongly.
 *
 * Two files with identical names are the one case position still decides, and
 * there the name cannot tell them apart either; they get -2, -3 suffixes.
 */
function meshId(name, seen) {
  const slug = String(name).toLowerCase().replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 64) || 'mesh'
  const n = (seen.get(slug) ?? 0) + 1
  seen.set(slug, n)
  return n === 1 ? `m-${slug}` : `m-${slug}-${n}`
}

/**
 * The frame the planning engine's exports are PROVEN to need — one constant, review finding: the
 * literal used to appear twice in this file, and the two sites feed different page families
 * (engine-composed anatomy scenes vs role-path splint/gallery pages). See resolveSpeScenes and
 * impliedFrame for the proof and the precedence story.
 */
export const SPE_LPS_FRAME = Object.freeze({ flipAP: false, lpsFrame: true })

/**
 * The ids buildLibrary will assign to these files, in this order — THE id assignment, exported so
 * a caller needing ids-before-overrides (the Builder's hashing effect) derives them structurally
 * instead of re-running buildLibrary and trusting a comment that the two stay in step.
 */
export function libraryIds(files) {
  const seen = new Map()
  return files.map((f) => meshId(typeof f === 'string' ? f : f.name, seen))
}

const keyOf = (role, stage) => `${role}/${stage}`

/**
 * The slots a page's OWN files fill.
 *
 * A page is built from the files the operator gave it, so each one stands for
 * the slot it classifies as — under both keys the resolver looks up, `role/stage`
 * and `role/*`. Two files of one role and stage: the first stands, and the page
 * shows both anyway (a recipe adds what it resolved, and generic pages draw every
 * file they were given).
 *
 * Returns an empty object for a page with no files of its own, which leaves
 * resolution exactly as it was.
 */
export function picksFromMeshes(library, meshIds) {
  const out = {}
  for (const id of meshIds ?? []) {
    const mesh = library.find((m) => m.id === id)
    if (!mesh) continue
    const slot = keyOf(mesh.role, mesh.stage)
    if (!(slot in out)) out[slot] = mesh.id
    const anyStage = `${mesh.role}/*`
    if (!(anyStage in out)) out[anyStage] = mesh.id
  }
  return out
}

/**
 * All library entries matching a (role, stage). Returns every match — deciding
 * between them is the caller's job, not this function's.
 */
export const candidates = (library, role, stage) =>
  library.filter((m) => m.role === role && m.stage === stage)

/**
 * A resolver over the library that records what it could not decide.
 *
 * `picks` maps 'role/stage' → mesh id, and is how the operator's answer gets
 * back in. Until they answer, an ambiguous slot stays UNRESOLVED — every page
 * needing it is skipped and listed, rather than built from a coin flip.
 */
function makeResolver(library, picks = {}, overrides = null) {
  const conflicts = new Map()
  // Every slot key asked for, in order. Page mode reports this per page, so the
  // operator's override panel offers what a page ACTUALLY consults — including
  // a `custom` recipe's, which live inside compose() and are declared nowhere.
  const asked = []
  // A page-level override, applied BEFORE auto-resolution and regardless of
  // how many candidates there are. That is the difference from `picks`, which
  // only chooses among rivals: an operator who says "this page shows the
  // cropped skull" means it even when auto-detection found exactly one skull,
  // or none. A pick naming a mesh no longer in the library is not applied —
  // it is recorded in `staleOverrides` and resolution falls back, rather than
  // the page silently losing a part the operator asked for.
  const overridesUsed = new Map()
  const staleOverrides = new Set()
  const overridden = (key) => {
    const id = overrides?.[key]
    if (!id) return null
    const mesh = library.find((m) => m.id === id)
    if (!mesh) { staleOverrides.add(key); return null }
    overridesUsed.set(key, mesh)
    return mesh
  }
  const resolve = (role, stage) => {
    const key = keyOf(role, stage)
    if (!asked.includes(key)) asked.push(key)
    const forced = overridden(key)
    if (forced) return forced
    const found = candidates(library, role, stage)
    if (found.length === 0) return null
    if (found.length === 1) return found[0]
    const chosen = found.find((m) => m.id === picks[key])
    if (!conflicts.has(key)) {
      conflicts.set(key, {
        key, role, stage,
        label: `${ROLE_LABELS[role] ?? role} (${stage})`,
        // 🔴 The distinguishing detail travels WITH the candidate. A
        // conflict the operator cannot choose on is not an option, it is a
        // dead end: two real skulls in one folder differ only by a `cropped`
        // flag and their vertex count, and a bare list of two filenames
        // makes them look interchangeable when they are not.
        candidates: found.map((m) => ({
          id: m.id, name: m.name, stage: m.stage, side: m.side,
          flags: m.flags ?? [],
        })),
        chosen: chosen?.id ?? null,
      })
    }
    return chosen ?? null              // unresolved until the operator picks
  }
  /**
   * Did this slot resolve to nothing BECAUSE it was ambiguous, rather than
   * because it is absent? The two are indistinguishable from `resolve`'s null,
   * and they mean opposite things to the operator: "your case does not have one"
   * versus "your case has two and I will not choose".
   */
  const wasAmbiguous = (role, stage) =>
    candidates(library, role, stage).length > 1 && !resolve(role, stage)
  /**
   * Resolve by ROLE ALONE, across every stage.
   *
   * For most roles the stage is load-bearing — a native skull and a post-plan
   * skull are the same role and must never be interchanged. For a few it carries
   * no information at all: an orbital difference model and a trial fitment model
   * are single-instance derived objects, and `buildLibrary` gives them different
   * stages depending on HOW they were classified — the filename's stage on the
   * automatic path, `stageForRole`'s on an operator override. So the same role
   * legitimately arrives as 'native' or 'output', and a role/stage lookup finds
   * it in one case and not the other.
   *
   * Measured in classify.test.js. The symptom would be a page silently vanishing
   * after an operator CORRECTED a role, which reads as a classification bug and
   * is not one.
   */
  const resolveByRole = (role) => {
    const key = `${role}/*`
    if (!asked.includes(key)) asked.push(key)
    const forced = overridden(key)
    if (forced) return forced
    const found = library.filter((m) => m.role === role)
    if (found.length === 0) return null
    if (found.length === 1) return found[0]
    const chosen = found.find((m) => m.id === picks[key])
    if (!conflicts.has(key)) {
      conflicts.set(key, {
        key, role, stage: '*',
        label: `${ROLE_LABELS[role] ?? role} (any stage)`,
        // 🔴 The distinguishing detail travels WITH the candidate. A
        // conflict the operator cannot choose on is not an option, it is a
        // dead end: two real skulls in one folder differ only by a `cropped`
        // flag and their vertex count, and a bare list of two filenames
        // makes them look interchangeable when they are not.
        candidates: found.map((m) => ({
          id: m.id, name: m.name, stage: m.stage, side: m.side,
          flags: m.flags ?? [],
        })),
        chosen: chosen?.id ?? null,
      })
    }
    return chosen ?? null
  }
  return { resolve, resolveByRole, wasAmbiguous, conflicts, asked, overridesUsed, staleOverrides }
}

/**
 * The page recipes.
 *
 * `needs` is a list of [role, stage] the page cannot be drawn without.
 * `wants` is a list it will use if present and simply omit if not — a splint
 * page is still a splint page without the skull behind it.
 *
 * Order here is the order in the report.
 */
const ORTHOGNATHIC_RECIPES = [
  {
    id: 'agreement', title: 'Agreement', kind: 'text',
    needs: [], build: () => AGREEMENT_BODY, styles: AGREEMENT_STYLES,
  },
  {
    // Reference p2. Drawn only when the caller supplies scan metadata: the
    // engine is pure and does not fetch, so the Builder passes what
    // /report-meta already returns for the STL/OBJ tool.
    id: 'source-data', title: 'Source data information', kind: 'info',
    needs: [], wants: [['skull', 'native']],
  },
  {
    // The planning engine's cephalometric analysis, when its report.json was added in the
    // Builder. Drawn only from supplied data, like source-data: the engine is pure and does not
    // fetch, and this page must carry the numbers a surgeon confirmed — never recompute them.
    id: 'cephalometry', title: 'Cephalometric analysis', kind: 'ceph',
    needs: [],
  },
  {
    id: 'preop', title: 'Preoperative Images', kind: 'scene',
    sceneName: 'Native Skull', topCaption: 'Native Skull', bottomCaption: 'Preoperative Images',
    needs: [['skull', 'native']], wants: [['mandible', 'native']],
    fitGroup: 'plan',
  },
  {
    id: 'comparison', title: 'Native vs Post Plan', kind: 'comparison',
    // The one page that is only honest when BOTH sides exist and are framed at
    // one camera scale — hence the shared fitGroup below.
    needs: [['skull', 'native'], ['skull', 'postplan']],
    leftCaption: 'Native Skull', rightCaption: 'Post Plan Skull',
    bottomCaption: 'Simulation Images',
  },
  {
    id: 'postplan', title: 'Post Plan Images', kind: 'scene',
    sceneName: 'Post Plan Skull', topCaption: 'Post Plan Skull', bottomCaption: 'Simulation Images',
    needs: [['skull', 'postplan']], wants: [['mandible', 'postplan']],
    fitGroup: 'plan',
  },
  {
    id: 'splint-intermediate', title: 'Intermediate Splint', kind: 'scene',
    sceneName: 'Intermediate Splint', topCaption: 'Intermediate Splint', bottomCaption: 'Simulation Images',
    needs: [['splint_intermediate', 'output']],
  },
  {
    id: 'splint-final', title: 'Final Splint', kind: 'scene',
    sceneName: 'Final Splint', topCaption: 'Final Splint', bottomCaption: 'Simulation Images',
    needs: [['splint_final', 'output']],
  },
  {
    id: 'splint-movement', title: 'Movement Splint', kind: 'scene',
    sceneName: 'Movement Splint', topCaption: 'Mandible Movement Splint', bottomCaption: 'Simulation Images',
    needs: [['splint_movement', 'output']],
  },
  {
    id: 'trial-fitment', title: 'Trial Fitment Models', kind: 'scene',
    sceneName: 'Trial Fitment Models', topCaption: 'Trial Fitment Models', bottomCaption: 'Simulation Images',
    // Either arch alone is a legitimate page — Case 3 ships two maxilla variants
    // and no mandible.
    needsAny: [['maxilla_teeth', 'trial_fitment'], ['mandible_teeth', 'trial_fitment']],
    needs: [],
  },
  {
    // Reference pp. 20-21: the deliverables, captioned. Built from whatever
    // fabrication outputs the case actually has.
    id: 'print-models', title: 'Print models', kind: 'gallery',
    needs: [],
    needsAny: [
      ['splint_final', 'output'], ['splint_intermediate', 'output'],
      ['splint_movement', 'output'],
      ['maxilla_teeth', 'trial_fitment'], ['mandible_teeth', 'trial_fitment'],
    ],
    topCaption: 'Print models',
  },
  // Two pages, exactly as delivered. NOT one long body: `textOps` runs its y
  // cursor down the page with no footer guard, so a body that does not fit runs
  // off the sheet — the same defect that put waiver notes through the footer of
  // the STL/OBJ report. Splitting at the source's own page break both matches the
  // reference and keeps each body inside one band. Guarded by a test.
  {
    id: 'terms-1', title: 'Terms and Conditions (1 of 2)', kind: 'text',
    needs: [], build: () => TERMS_BODY_1, styles: TERMS_STYLES,
  },
  {
    id: 'terms-2', title: 'Terms and Conditions (2 of 2)', kind: 'text',
    needs: [], build: () => TERMS_BODY_2, styles: TERMS_STYLES,
  },
]

export const RECIPES = {
  orthognathic: ORTHOGNATHIC_RECIPES,
  orbital: ORBITAL_RECIPES,
  fibula: FIBULA_RECIPES,
}


/**
 * Draft a report from a classified library.
 *
 * @param {Array} library      from buildLibrary()
 * @param {object} opts
 * @param {'orthognathic'|'fibula'} [opts.template]
 * @param {object} [opts.meta]  surgeon/patient/case fields
 * @param {object} [opts.picks] 'role/stage' → mesh id, resolving ambiguity
 * @returns {{doc: object, notes: Array, conflicts: Array, sceneParts: object}}
 *   `notes`   — every page NOT built, and exactly why.
 *   `conflicts` — slots with more than one candidate, awaiting an operator pick.
 *   `sceneParts` — sceneId → [{meshId, name, color, handle}], what the renderer
 *   must load. Kept OUT of the saved document, which stores only meshIds.
 */
/**
 * The planning engine's scene compositions, resolved to THIS library's files.
 *
 * Why the plan's scenes beat role reconstruction: the engine's native anatomy is per-bone (five
 * separate meshes, no composite "native skull" file), and its post-plan scene deliberately MIXES
 * stages — structures the plan never moved (teeth, the canal) ride into the post-plan picture
 * beside the moved bones. Both compositions are exactly what the surgeon saw on screen, and
 * neither can be rebuilt from role/stage slots: the first has no file to resolve, the second
 * would need to know which native meshes the plan considered unchanged. So when the report
 * travels with the case, its scenes are the authority for the anatomy pages, and the role-based
 * recipes stay as the fallback for every scene that cannot be resolved here.
 *
 * A scene resolves only when EVERY visible part's mesh was content-hash matched to a supplied
 * file — a partial composition would silently show less anatomy than the plan did. Splint and
 * guide scenes are left to the house recipes on purpose: the delivered report shows an appliance
 * alone, which is the design app's format to own.
 *
 * Colours come from the plan VERBATIM — they are the shared palette, chosen when the scene was
 * composed — and `neutral` derives from the matched file's role exactly as partFor derives it.
 *
 * @returns {{native: object|null, postplan: object|null}|null}
 */
export function resolveSpeScenes(spe, library) {
  const scenes = Array.isArray(spe?.doc?.scenes) ? spe.doc.scenes : []
  const matched = Array.isArray(spe?.matched) ? spe.matched : []
  if (!scenes.length || !matched.length) return null
  const fileByEntry = new Map(matched.map((m) => [m.entryId, m.fileId]))
  const refById = new Map(library.map((m) => [m.id, m]))
  const speLib = new Map((spe.doc.library ?? []).map((e) => [e.id, e]))

  const out = { native: null, postplan: null }
  for (const s of scenes) {
    const parts = []
    let ok = true
    let hasPostplan = false
    let hasAppliance = false
    for (const p of s.parts ?? []) {
      if (p.visible === false) continue
      const ref = refById.get(fileByEntry.get(p.meshId))
      if (!ref) { ok = false; break }
      const entry = speLib.get(p.meshId) ?? {}
      if ((entry.stage ?? 'native') === 'postplan') hasPostplan = true
      // An APPLIANCE is a role whose stage is settled as 'output' (splints, guides, trays) —
      // NOT isNonAnatomy, which is the render-tone set and includes the teeth: using it here
      // disqualified every real scene, since both of the plan's anatomy compositions carry teeth.
      if (entry.stage === 'output' || stageForRole(entry.role ?? ref.role) === 'output') hasAppliance = true
      parts.push({
        meshId: ref.id, name: ref.name, handle: ref.handle, role: ref.role,
        color: p.color, neutral: isNonAnatomy(ref.role),
      })
    }
    if (!ok || !parts.length) continue
    // The appliance-part veto holds REGARDLESS of any declared kind — a scene containing an
    // output-stage part must never occupy a plan slot, whatever the doc claims (re-review: a
    // declared kind used to bypass this, so a mislabelled splint scene could render captioned
    // as Native Skull). Then the DECLARED kind selects the slot (the engine states it at
    // construction since 2026-09-01); stage inference remains only for pre-field docs; an
    // UNKNOWN declared kind skips to the role path, never defaults into a plan slot.
    if (hasAppliance) continue
    const kind = s.kind ?? (hasPostplan ? 'postplan' : 'native')
    if (kind !== 'native' && kind !== 'postplan') continue
    const slot = kind
    // The engine's meshes are in the CT's LPS frame — PROVEN on a real case, not assumed: the
    // plan's 5 mm maxillary advance displaced the upper teeth along −Y (anterior = −Y), and the
    // chin its analysis measured 1.4 mm to the patient's RIGHT sits at −1.42 mm X (right = −X).
    // The global look default (flipAP, tuned to the team's RAS exports) shows the occiput under
    // an A badge and the wrong side under each lateral for these meshes, so the frame is pinned
    // HERE and rendered per scene (renderScenes honours it). Pinned rather than read from the
    // doc: report.json used to declare flipAP: true, which the emitter never verified and the
    // geometry refutes.
    if (!out[slot]) out[slot] = { name: s.name, parts, frame: SPE_LPS_FRAME }
  }
  return (out.native || out.postplan) ? out : null
}

export function draftReport(library, {
  template = 'orthognathic', meta = {}, picks = {}, ctInfo = {}, ios = {}, ceph = null, mode = 'auto',
  // Orbital: per-mesh volume analysis keyed 'role/stage' → {cc, watertight, filename},
  // and the operator's quality-waiver entry. Both are supplied, never computed here —
  // draftReport is pure and does not read files.
  volumes = {}, waiver = null,
  // meshId → the operator's declaration: a preset id, an appearance, or a map of
  // POLYGROUP name → either. Absent means "no operator opinion" and the role decides.
  parts: partRoles = {},
  // The planning engine's report, when it travels with the case: `{doc, matched}` — the parsed
  // report.json and speGeometry's content-hash matches. Lets the anatomy pages be built from the
  // PLAN'S OWN scene compositions rather than reconstructed from roles; see resolveSpeScenes.
  spe = null,
} = {}) {
  // A page-numbered folder decides its own page order; roles only decide
  // colours. Running the role-based recipes over the reference corpus found
  // five rival "native skulls" — one per page that shows one — and produced a
  // report with no images at all. See pageDraft.js.
  if (mode === 'page' || (mode === 'auto' && isPageOrganised(library))) {
    // Both cross this boundary. `picks` because the source-data page has SLOTS an
    // operator may have to resolve; `parts` because the page-organised path draws
    // the same meshes and must honour the same statement about what is fabricated.
    const paged = draftFromPages(library, { template, meta, picks, parts: partRoles })
    // Review finding: this early return used to DROP a supplied cephalometric analysis with no
    // note — a 'Page N'-named delivery folder plus report.json showed a green 'Cephalometry
    // loaded' message and then a draft with no ceph page. The analysis is page-order-independent,
    // so it rides along here exactly as in the recipe path: verbatim, after the pages.
    if (hasCephAnalysis(ceph)) {
      const cephPage = newCephPage(makeIdFactory('spe-')('p'), { ceph })
      cephPage.title = 'Cephalometric analysis'
      cephPage.recipeId = 'cephalometry'
      paged.doc.pages.push(cephPage)
    }
    return paged
  }

  const recipes = RECIPES[recipesKeyFor(template)] ?? RECIPES[template]
  if (!recipes) {
    const known = formatFor(template)
    throw new Error(known
      ? `draftReport: the "${known.label}" format has no pages that fill themselves`
      : `draftReport: unknown report format "${template}"`)
  }
  if (!Array.isArray(library)) {
    throw new TypeError('draftReport: library must be an array from buildLibrary()')
  }

  // Everything below is page mode's, run over this template's full recipe order
  // with no instance ids. That is exactly the old whole-report behaviour, and the
  // caseReport golden is the proof — see draftPages.
  return draftPages(library, {
    template, meta, picks, ctInfo, ios, ceph, volumes, waiver, parts: partRoles, spe,
    pages: recipes.map((r) => ({ typeId: r.id })),
  })
}

/**
 * Draft a report from an ORDERED LIST OF PAGE TYPES — page mode.
 *
 * The operator picks pages one at a time and the report is the list. Each
 * entry is `{ instanceId, typeId, picks }`:
 *
 *   typeId      a page type of this template (pageTypes.js) — a recipe id.
 *   instanceId  this page's own identity, minted when it was added and never
 *               reused. Copied onto every page and note the entry produces, so
 *               an edit follows the page the operator made it on, and the same
 *               type can appear twice. Omitted by draftReport, whose pages have
 *               no instance and must stay byte-identical to what they were.
 *   picks       'role/stage' → mesh id for THIS page only. An OVERRIDE, not a
 *               tie-break: it wins even where auto-detection found one mesh or
 *               none. Report-wide `picks` keep their old meaning — they only
 *               choose among rivals — and a page's own pick outranks them.
 *
 * 🔴 ONE pass over the whole list, never pages drafted apart and merged. One
 * id factory keeps page and scene ids unique; one scene map lets two pages that
 * show the same meshes share one render instead of shooting it twice; one doc
 * means one layout pass, which is what makes "Page 03 of 21" right by
 * construction. Merging separately drafted documents breaks all three.
 *
 * @returns {{doc, notes, conflicts, sceneParts, pages?}}
 *   `pages` is present only when entries carry instance ids: per page, the
 *   slots it actually asked the library for, the overrides applied (flagging
 *   any that fill a slot with a mesh of another role), overrides naming a mesh
 *   that is gone, the conflicts it raised, and how many sheets it built.
 */
export function draftPages(library, {
  template = 'orthognathic', pages = [], meta = {}, picks = {}, ctInfo = {}, ios = {},
  ceph = null, volumes = {}, waiver = null, parts: partRoles = {}, spe = null,
  // 🔴 OFF for the report, ON only for a single-page download.
  //
  // A page the Builder cannot finish is normally skipped, so the report never
  // claims to hold it — the team makes that page and merges it in. With this
  // set, such a page is drafted as a WORKING SHEET instead: the chrome, the
  // heading and its renders, drawing area empty, for the team to draw on in
  // GIMP.
  //
  // It must stay off for the report. Turning it on there would put a
  // half-finished page into the delivered PDF and would move the golden hashes.
  // Only `onePage()` in CaseReportBuilder.jsx sets it.
  handSheets = false,
} = {}) {
  const format = formatFor(template)
  const recipes = RECIPES[recipesKeyFor(template)] ?? RECIPES[template] ?? []
  if (!format && !RECIPES[template]) {
    throw new Error(`draftPages: "${template}" is not a report format`)
  }
  if (!Array.isArray(library)) {
    throw new TypeError('draftPages: library must be an array from buildLibrary()')
  }
  // The template's own recipes (suggested pages) and the generic page types
  // that work in every template (genericPages.js). Generic ids carry a `g-`
  // prefix, so neither can shadow the other.
  // The generic page types, this format's own recipes, and the pages of its
  // template (formats.js) — which may rename a recipe under the template page's
  // key, or carry the template's caption presets on a generic page.
  const recipeById = new Map(
    [...GENERIC_PAGES, ...recipes, ...formatPageTypes(template, RECIPES)].map((r) => [r.id, r]),
  )

  const doc = newReport({ template, meta })
  doc.library = library.map(({ handle, ...ref }) => ref)   // handles are not document data

  const nextId = makeIdFactory('')
  const notes = []
  const sceneParts = {}
  // Scene identity is its CONTENT, not the recipe that asked for it. Case 2's
  // Native Skull is wanted by both the preoperative page and the comparison
  // page; without this it became two scenes holding the same two meshes, and
  // the renderer shot all six views of each — twice the wall-clock for
  // byte-identical pictures. Keyed on parts + name + fit group, so two scenes
  // that must be framed differently stay separate.
  const sceneBySignature = new Map()

  // A scene is a named set of coloured meshes. `fitGroup` decides which scenes
  // share ONE camera scale: 'plan' ties Native and Post Plan together so the two
  // columns of the comparison page are measurable against each other. A splint
  // scene fits itself — it is a different object, not a different state of the
  // same one.
  const addScene = (name, parts, fitGroup, frame = undefined) => {
    const signature = `${name}::${fitGroup ?? ''}::${JSON.stringify(frame ?? null)}::${parts.map((p) => p.meshId).join('|')}`
    const existing = sceneBySignature.get(signature)
    if (existing) return existing
    const id = nextId('s')
    doc.scenes.push(newScene(id, name, parts.map((p) => newScenePart(p.meshId, p.color)), { fitGroup, frame }))
    sceneParts[id] = parts
    sceneBySignature.set(signature, id)
    return id
  }
  const partFor = (mesh) => ({
    meshId: mesh.id, name: mesh.name, handle: mesh.handle,
    // `role` rides along for the neutral decision. It is sceneParts data, which
    // the renderer consumes and the saved document never stores.
    role: mesh.role,
    // Whether this file's content was hash-matched to the planning engine's report — it decides
    // the scene's camera frame below, because a verified file IS an engine export and engine
    // exports are LPS.
    speVerified: mesh.speVerified,
    // Colour and the neutral flag together, because they must move as one: the
    // colour is the fallback the renderer uses when the file carries no
    // meaningful paint. Honours an operator declaration, and may carry
    // per-POLYGROUP maps for a file holding a part AND the anatomy under it.
    ...partAppearance(paletteFor(template) ?? template, mesh, partRoles[mesh.id]),
  })

  // ── the two grid-page shapes, assembled in ONE place ─────────────────────
  // Review finding: the SPE-scene branch and the role path each carried private copies of these
  // assemblies; a change to page shape (badge mode, grid dims, caption slots) made in one copy
  // would silently give SPE-composed and folder-only reports different layouts, untested by
  // either suite. The recipes differ in WHERE parts come from — never in the page shape.
  const pushSixViewPage = (sceneId, { tc = '', bc = '' }, recipeId) => {
    const page = sixViewGridPage(nextId('p'), sceneId)
    page.captions.tc = tc
    page.captions.bc = bc
    page.recipeId = recipeId
    doc.pages.push(page)
    return page
  }
  const pushComparisonPage = (left, right, { tl = '', tr = '', bc = '' }, recipeId, views) => {
    const page = comparisonGridPage(nextId('p'), left, right, views ?? ['A', 'RL', 'LL'])
    page.captions.tl = tl
    page.captions.tr = tr
    page.captions.bc = bc
    page.recipeId = recipeId
    doc.pages.push(page)
    return page
  }

  // The frame a ROLE-PATH scene should pin, from what it is made of. The engine-composed scenes
  // above pin their frame explicitly; pages the role path builds (a splint page, a gallery) had
  // no equivalent, so on an engine case they rendered under the operator's global look — tuned to
  // the team's RAS exports, wrong for the engine's LPS meshes, and unnoticeable on a
  // near-symmetric wafer. When EVERY part of a scene was content-verified against the engine's
  // report, the scene is made of engine exports and gets the proven LPS frame; one unverified
  // part means mixed provenance, and the operator's global setting stands (a scene renders under
  // ONE camera set — there is nothing coherent to do for a genuine mix).
  const impliedFrame = (parts) =>
    (parts.length && parts.every((p) => p.speVerified) ? SPE_LPS_FRAME : undefined)

  const missingLabel = ([role, stage]) => `${ROLE_LABELS[role] ?? role} (${stage})`

  // The plan's own scene compositions, when its report travels with the case. Resolved ONCE;
  // consumed by the three anatomy recipes below. See resolveSpeScenes for why they are the
  // authority and the role-based path is the fallback.
  const speScenes = resolveSpeScenes(spe, library)

  // Conflicts are raised per page now, and gathered here once per slot in the
  // order first raised — the order the single shared resolver used to produce,
  // so the whole-report path returns exactly the list it always did.
  const conflictsByKey = new Map()
  const perPage = []
  const pageMode = pages.some((e) => e.instanceId != null)

  for (const entry of pages) {
    // `let`, because a hand page drafted as a working sheet is swapped for the
    // sheet layout its own template variant states. See the `recipe.hand`
    // branch below.
    // RENAMED_PAGE_TYPES: a basket saved under an id that has since been renamed
    // still resolves. The Builder's own list goes through genericPage(), which
    // has always honoured it; this path did not, so the page was silently left
    // out of the report while the Builder still offered it.
    let recipe = recipeById.get(entry.typeId) ?? recipeById.get(RENAMED_PAGE_TYPES[entry.typeId])
    if (!recipe) {
      // A saved list naming a type that no longer exists. Said, not dropped.
      notes.push({
        page: String(entry.typeId),
        kind: 'skipped',
        reason: `not built — "${entry.typeId}" is not a page type of the `
          + `${format?.label ?? template} format`,
        missing: [],
        ...(entry.instanceId != null ? { instanceId: entry.instanceId } : {}),
      })
      continue
    }
    // A hand page is skipped unless a WORKING SHEET was asked for AND its own
    // template variant says how to lay one out. No `sheet` means no download:
    // the seven text pages (the helmet leaflet, the QR page) carry no renders,
    // and a chrome-only page would need a text builder this does not have.
    if (recipe.hand && (!handSheets || !recipe.sheet)) {
      // A page of the template the Builder cannot draw. Listed so the report's
      // page list matches the template; said here so a stored list naming one
      // does not silently produce a shorter document.
      notes.push({
        page: recipe.title,
        kind: 'skipped',
        reason: `not built — ${recipe.hand}`,
        missing: [],
        ...(entry.instanceId != null ? { instanceId: entry.instanceId } : {}),
      })
      continue
    }
    if (recipe.hand) {
      // A WORKING SHEET for the team to draw on, not a finished page.
      //
      // The layout comes from the page's own template variant — `sheet` in
      // formats.js — so the downloaded page is laid out the way the team's own
      // template lays it out. A page with no `sheet` is chrome only, which is
      // what the text pages want: an empty `text` body emits no ops, so the
      // heading, the frame and the footer are drawn and the band is left clear.
      //
      // The note stays, and says what is still owed. A sheet that arrived
      // looking like a finished page is the one thing this must not do.
      const sheet = recipeById.get(recipe.sheet)
      if (!sheet) {
        throw new Error(`draftPages: "${recipe.id}" declares sheet "${recipe.sheet}", `
          + 'which is not a page type')
      }
      notes.push({
        page: recipe.title,
        kind: 'sheet',
        reason: `a working sheet to draw on — ${recipe.hand}`,
        missing: [],
        ...(entry.instanceId != null ? { instanceId: entry.instanceId } : {}),
      })
      // The sheet's LAYOUT, under the template page's own identity — so the
      // download is named and headed as the page it stands in for, not as
      // "Six views".
      recipe = {
        ...sheet,
        id: recipe.id,
        title: recipe.title,
        listTitle: recipe.listTitle,
        templatePage: recipe.templatePage,
        captions: { ...(sheet.captions ?? {}), tc: recipe.title },
      }
    }
    // The files this page was given ARE what it shows. A page is built from its
    // own files (the operator chooses them per page), so they become this page's
    // slot overrides: a Native skull page built from one skull draws that skull,
    // not whichever skull another page added. A per-slot pick the operator made
    // by hand still wins, and a page with no files of its own resolves from the
    // report's library exactly as it always did — which is the path the golden
    // documents draft.
    // The files this page was given ARE the page. A page built from its own
    // files resolves ONLY among them: it can never quietly draw a mesh another
    // page added, and a page handed the wrong file says what it is missing
    // rather than filling the gap from the report. A page with no files of its
    // own resolves from the whole library exactly as it always did — the path
    // the golden documents draft.
    // A page HAS its own files as soon as the operator gave it any — including
    // after they took the last one off again. Emptying the list must leave the
    // page asking for files, not quietly resolving from what other pages added.
    const hasOwnFiles = Array.isArray(entry.inputs?.meshes)
    const ownIds = new Set(entry.inputs?.meshes ?? [])
    // A per-slot pick the operator made by hand names a mesh deliberately, so it
    // belongs to this page too even if the page's files do not include it.
    const pickedIds = new Set(Object.values(entry.picks ?? {}))
    const pageLibrary = hasOwnFiles
      ? library.filter((m) => ownIds.has(m.id) || pickedIds.has(m.id))
      : library
    const ownPicks = { ...picksFromMeshes(pageLibrary, entry.inputs?.meshes), ...(entry.picks ?? {}) }
    const entryOverrides = Object.keys(ownPicks).length > 0
    const res = makeResolver(pageLibrary, picks, entryOverrides ? ownPicks : null)
    const { resolve, resolveByRole, wasAmbiguous } = res
    // A second resolver over the WHOLE case, for the facts that are about the
    // case rather than this page (see the compose context below). Its conflicts
    // are deliberately NOT merged into the report's: the page's own resolver is
    // what the operator answers, and a case-wide read of the leg files must not
    // raise a question on a page that shows no leg.
    const { resolve: resolveCase } = makeResolver(library, picks, null)
    const pagesBefore = doc.pages.length
    const notesBefore = notes.length

    // A one-pass loop, so the recipe body's own `continue`s end THIS page and
    // fall through to the bookkeeping below instead of skipping it. The body is
    // the old whole-report loop's, moved verbatim.
    for (const r of [recipe]) {
      // ── anatomy pages from the PLAN'S scenes, when they resolve ─────────────
      // House captions and the shared 'plan' fit group are kept — the delivered
      // report's language and its equal-scale guarantee are this app's to own;
      // only the COMPOSITION comes from the plan. addScene's signature dedup
      // means the preop page and the comparison's left column share one scene,
      // so the renderer shoots each composition once.
      // Matched on the RECIPE, not on the page key: a template page may use a
      // recipe under its own key (the orthognathic P/S/I comparison page), and
      // matching the key would build the two comparison pages of one report from
      // different meshes and a different frame.
      const rid = r.recipeId ?? r.id
      // `!hasOwnFiles` as well as `!entryOverrides`: the plan's compositions are
      // resolved once over the WHOLE library, so a page that carries its own
      // files but whose files resolve to nothing — the operator took the last
      // one off, or the browser reloaded and dropped them — would otherwise
      // draw the plan's six meshes, none of which it was given. entryOverrides
      // alone does not catch that: it is false precisely when nothing resolved.
      if (speScenes && !entryOverrides && !hasOwnFiles
        && (rid === 'preop' || rid === 'postplan' || rid === 'comparison')) {
        const native = speScenes.native
        const postplan = speScenes.postplan
        if (rid === 'comparison' && native && postplan) {
          const left = addScene('Native Skull', native.parts, 'plan', native.frame)
          const right = addScene('Post Plan Skull', postplan.parts, 'plan', postplan.frame)
          pushComparisonPage(left, right,
            { tl: r.leftCaption, tr: r.rightCaption, bc: r.bottomCaption }, r.id, r.views)
          continue
        }
        const src = rid === 'preop' ? native : rid === 'postplan' ? postplan : null
        if (src) {
          const sceneId = addScene(rid === 'preop' ? 'Native Skull' : 'Post Plan Skull',
            src.parts, 'plan', src.frame)
          pushSixViewPage(sceneId, { tc: r.topCaption, bc: r.bottomCaption }, r.id)
          continue
        }
        // No usable plan scene for THIS page — the role/stage path below decides,
        // with its existing skip notes; the Builder's gap list already names any
        // missing file by the plan's own label.
      }
      // ── custom: the recipe composes its own page, with the shared tools ────
      //
      // Added for the orbital template, whose six pages each need something the
      // generic kinds do not express — a CT block with styled rows and a waiver, a
      // colour-keyed volume legend, per-view crops, recipe-authored callouts. The
      // alternative was six new `kind`s in this loop, each used once; this keeps
      // the shared machinery here (resolution, conflicts, scenes, notes) and the
      // template's specifics in its own file.
      //
      // A compose function returns nothing and pushes what it needs. It cannot
      // reach past these tools, so it cannot invent a page that skips the
      // conflict or note discipline.
      if (r.kind === 'custom') {
        r.compose({
          // `entry` carries the page's own inputs — the meshes and view a generic
          // page shows. Recipes ignore it.
          recipe: r, entry, doc, notes, library: pageLibrary, meta, ctInfo, volumes, waiver,
          resolve, resolveByRole, wasAmbiguous,
          candidates: (role, stage) => candidates(pageLibrary, role, stage),
          addScene, partFor, nextId, impliedFrame, missingLabel,
          pushSixViewPage, ROLE_LABELS,
          // ── the WHOLE case, for facts that are not about this page ──────────
          // Which leg the fibula came from is a fact about the CASE. It is read
          // off the leg and fibula files, and the pages that state it — the
          // reconstructed mandible, the post-plan skull — show neither. Once
          // every page resolved inside its own files (ADR 0017), those pages
          // stopped being able to see the evidence: the sentence silently
          // vanished, or worse, a page given one leg stated a side on a case
          // whose files disagree with each other. The same applies to the
          // orbital planned side, which is decided by which post-plan volume
          // the CASE holds.
          //
          // So a page is still drawn from its own files, and still asks the
          // case about the case.
          caseLibrary: library,
          resolveCase,
        })
        continue
      }

      // ── text pages: no parts, always available ──────────────────────────────
      if (r.kind === 'text') {
        const tp = newTextPage(nextId('p'), r.build())
        if (r.styles) tp.styles = r.styles
        // The recipe id is this page's STABLE identity across redrafts — see
        // edits.js. `page.id` is positional and must never be used for that.
        tp.recipeId = r.id
        doc.pages.push(tp)
        continue
      }

      // ── what does this page need, and did we get it? ────────────────────────
      const required = (r.needs ?? []).map((spec) => [spec, resolve(...spec)])
      const missing = required.filter(([, m]) => !m).map(([spec]) => spec)
      let anyOk = true
      let anyParts = []
      if (r.needsAny) {
        anyParts = r.needsAny.map((spec) => resolve(...spec)).filter(Boolean)
        anyOk = anyParts.length > 0
      }
      if (missing.length || !anyOk) {
        const absent = missing.length ? missing : r.needsAny
        // An ambiguous slot resolves to null exactly like an absent one, so the
        // status is reported PER SLOT. Saying it per note was wrong and actively
        // misleading: Case 4 needs both skulls, has two natives and no post-plan,
        // and a single verdict rendered that as "Skull (native) and Skull
        // (postplan) is ambiguous" — sending the operator to look for two
        // post-plan skulls that do not exist.
        const why = (spec) => {
          const n = candidates(pageLibrary, ...spec).length
          return n > 1
            ? `${missingLabel(spec)} is ambiguous (${n} candidates — choose one)`
            : `${missingLabel(spec)} is not in this case`
        }
        notes.push({
          page: r.title,
          kind: 'skipped',
          reason: missing.length
            ? `not built — ${missing.map(why).join('; ')}`
            : `not built — none of ${r.needsAny.map(missingLabel).join(', ')} found in this case`,
          missing: absent.map(missingLabel),
        })
        continue
      }

      const got = required.map(([, m]) => m)
      const optional = (r.wants ?? []).map((spec) => resolve(...spec)).filter(Boolean)

      // Which slots did this page go ahead WITHOUT because they were ambiguous?
      // Hoisted above the comparison branch: it used to sit below, after that
      // branch's `continue`, so a comparison page whose mandible was ambiguous
      // dropped it and said nothing — the exact defect this check exists to
      // prevent, surviving in the one branch it did not cover.
      const reportDropped = (specs) => {
        const dropped = specs.filter((spec) => wasAmbiguous(...spec))
        if (!dropped.length) return
        notes.push({
          page: r.title,
          kind: 'incomplete',
          reason: `built WITHOUT ${dropped.map(missingLabel).join(' and ')} — `
            + 'more than one candidate; choose which mesh to use and re-draft',
          missing: dropped.map(missingLabel),
        })
      }

      // ── info: the Source data information page (reference p2) ──────────────
      if (r.kind === 'info') {
        // Labels come from sseReport's CT_FIELDS — the SAME list the STL/OBJ tool
        // prints — so the two reports can never disagree about what a CT field is
        // called. `core` is exactly the subset the reference page shows.
        const rows = CT_FIELDS
          .filter(([, , o]) => o && o.core)
          .map(([label, key]) => [label, ctInfo?.[key]])
          .filter(([, v]) => v != null && v !== '')
        const iosRows = IOS_FIELDS
          .map(([label, key]) => [label, ios?.[key]])
          .filter(([, v]) => v != null && v !== '')
        if (!rows.length && !iosRows.length) {
          notes.push({
            page: r.title,
            kind: 'skipped',
            reason: 'not built — no scan metadata was supplied for this case',
            missing: ['scan metadata'],
          })
          continue
        }
        const groups = []
        if (rows.length) groups.push({ title: 'Skull data', rows })
        if (iosRows.length) groups.push({ title: 'Intraoral scan', rows: iosRows })
        // The master's source-data page has NO render slot (template.js `sourceData`), so
        // none is drafted — a render nobody draws was still being paid for (G3 review, 28 Sep).
        // The case summary (FIBULA p2) is the planner's own sentence, typed on the page
        // (edits.fields.summary); it is never derived from a mesh.
        const page = newInfoPage(nextId('p'), { groups })
        // The heading is drawn INSIDE the box by infoOps, as the reference has it.
        // Setting captions.tc as well put it on the frame rule.
        page.title = r.topCaption ?? r.title
        // The quality waiver, the same builder the other two source-data pages use.
        // Without it the Builder's waiver panel took a tick and notes and this page
        // printed neither — the operator's own statement about the scan, dropped.
        const wv = waiverBlockFor(waiver)
        if (wv) page.waiverBlock = wv
        page.recipeId = r.id
        doc.pages.push(page)
        continue
      }

      // ── ceph: the planning engine's analysis, verbatim ──────────────────────
      if (r.kind === 'ceph') {
        if (!hasCephAnalysis(ceph)) {
          notes.push({
            page: r.title,
            kind: 'skipped',
            reason: 'not built — no cephalometric analysis was supplied. Add the planning '
              + "engine's report.json to include it.",
            missing: ['planning engine report'],
          })
          continue
        }
        const page = newCephPage(nextId('p'), { ceph })
        page.title = r.title
        page.recipeId = r.id
        doc.pages.push(page)
        continue
      }

      // ── gallery: the print-models pages (reference pp. 20-21) ───────────────
      if (r.kind === 'gallery') {
        const items = anyParts.map((mesh) => {
          const galleryParts = [partFor(mesh)]
          const sceneId = addScene(ROLE_LABELS[mesh.role] ?? mesh.role, galleryParts, null, impliedFrame(galleryParts))
          return {
            sceneId,
            view: 'ISO',
            caption: ROLE_LABELS[mesh.role] ?? mesh.role,
            annotations: [],
          }
        })
        if (!items.length) continue
        // Paged as the delivered reports page them (galleryPaging): up to six on a page, two
        // columns from three up — ORTHO p15 carries five print models on one page. The count is
        // BALANCED across the pages that needs, so a continuation page never holds one render
        // in a sea of white. Three to a page, stacked, sent a fourth model to its own page
        // (Deepak, 28 Sep).
        const { perPage, cols } = galleryPaging(items.length)
        for (let i = 0; i < items.length; i += perPage) {
          const page = newGalleryPage(nextId('p'), { items: items.slice(i, i + perPage), cols })
          page.captions.tc = i === 0 ? (r.topCaption ?? r.title) : `${r.topCaption ?? r.title} (cont.)`
          page.recipeId = i === 0 ? r.id : `${r.id}#${i / perPage}`
          doc.pages.push(page)
        }
        continue
      }

      if (r.kind === 'comparison') {
        // Two scenes, one fit group, six cells alternating left/right.
        //
        // Resolved BY NAME, not by unpacking `got` positionally. `got` follows the
        // order of `needs`, so `const [native, postplan] = got` silently swapped
        // the two columns the moment anyone reordered that array — a comparison
        // page with the sides reversed under correct-looking captions, which is
        // the worst kind of wrong on a surgical document.
        const native = resolve('skull', 'native')
        const postplan = resolve('skull', 'postplan')
        const nativeMand = resolve('mandible', 'native')
        const planMand = resolve('mandible', 'postplan')
        reportDropped([['mandible', 'native'], ['mandible', 'postplan']])
        const leftParts = [native, nativeMand].filter(Boolean).map(partFor)
        const rightParts = [postplan, planMand].filter(Boolean).map(partFor)
        const left = addScene('Native Skull', leftParts, 'plan', impliedFrame(leftParts))
        const right = addScene('Post Plan Skull', rightParts, 'plan', impliedFrame(rightParts))
        pushComparisonPage(left, right,
          { tl: r.leftCaption, tr: r.rightCaption, bc: r.bottomCaption }, r.id, r.views)
        continue
      }

      // ── an ordinary six-view scene page ─────────────────────────────────────
      //
      // A page that gets BUILT can still be missing a part. `wants` and `needsAny`
      // both resolve to null when a slot is ambiguous, and the page goes ahead
      // without it — which is how Case 3's Trial Fitment page rendered the lower
      // arch alone while two maxilla meshes sat unchosen in the library. It looked
      // finished. Nothing on the page said otherwise.
      reportDropped([...(r.wants ?? []), ...(r.needsAny ?? [])])

      const parts = [...got, ...optional, ...anyParts].map(partFor)
      const sceneId = addScene(r.sceneName, parts, r.fitGroup, impliedFrame(parts))
      pushSixViewPage(sceneId, { tc: r.topCaption, bc: r.bottomCaption }, r.id)
    }

    // A file the page was given that did not reach it. Two files of one role and
    // stage is the ordinary way in: a recipe fills its slot from the first and
    // the second is simply not drawn. The page row says "2 files"; without this
    // the page would show one and say nothing.
    // A page type that takes no meshes at all (the CT data page since the master, whose
    // source-data page has no picture slot) cannot have been built "without" one: files
    // left on such an entry from an earlier list are simply not its business.
    const takesMeshes = Array.isArray(recipe.inputs) ? recipe.inputs.includes('meshes') : true
    if (hasOwnFiles && takesMeshes && doc.pages.length > pagesBefore) {
      // The SAME five containers renderScenes.js walks — a scene reaches the
      // paper through any of them, and an info page (CT data, orthognathic
      // source data, the orbital preview) holds its render in `images`. Reading
      // only `cells` and `items` told the operator every one of those pages was
      // built without the file it had just drawn. `Array.isArray` on each,
      // because a grid page's `rows` is the row COUNT, not a list.
      const each = (v) => (Array.isArray(v) ? v : [])
      const drawn = new Set(doc.pages.slice(pagesBefore).flatMap((pg) => [
        ...each(pg.cells), ...each(pg.images), ...each(pg.items),
        ...each(pg.steps), ...each(pg.rows),
      ].flatMap((c) => sceneParts[c?.sceneId]?.map((part) => part.meshId) ?? [])))
      // A file no longer in the library — the reload case — is not this note's
      // business. The skip path already says "choose again", and naming it here
      // printed a raw internal mesh id at the operator, twice over.
      const unused = [...ownIds]
        .map((id) => library.find((m) => m.id === id))
        .filter((m) => m && !drawn.has(m.id))
      // WHY it was not drawn decides what the operator does about it, so the
      // two causes are said apart. A second file of a kind the page already
      // shows is the ordinary way in and needs nothing. A kind this page never
      // shows is a file on the wrong page — telling them it was "a second file
      // of the same kind" sent them looking for a duplicate that did not exist.
      const kindOf = (m) => keyOf(m.role, m.stage)
      const drawnKinds = new Set([...drawn]
        .map((id) => library.find((m) => m.id === id))
        .filter(Boolean).map(kindOf))
      const say = (group, why) => {
        if (!group.length) return
        const names = group.map((m) => m.name)
        notes.push({
          page: recipe.title,
          kind: 'incomplete',
          reason: `built WITHOUT ${names.join(', ')} — ${why}`,
          missing: names,
          ...(entry.instanceId != null ? { instanceId: entry.instanceId } : {}),
        })
      }
      say(unused.filter((m) => drawnKinds.has(kindOf(m))),
        'this page shows one file per slot, so a second file of the same kind is not shown')
      say(unused.filter((m) => !drawnKinds.has(kindOf(m))),
        'this page does not show that kind of file')
    }

    // A template whose pages carry their own header title (the image sets: a
    // CT page heads "Preview Image", a comparison page "Comparison Images").
    if (recipe.headerTitle) {
      for (const pg of doc.pages.slice(pagesBefore)) pg.headerTitle ??= recipe.headerTitle
    }

    for (const [key, c] of res.conflicts) {
      if (!conflictsByKey.has(key)) conflictsByKey.set(key, c)
    }
    if (entry.instanceId != null) {
      for (const pg of doc.pages.slice(pagesBefore)) pg.pageInstanceId = entry.instanceId
      for (const n of notes.slice(notesBefore)) n.instanceId = entry.instanceId
      perPage.push({
        instanceId: entry.instanceId,
        typeId: entry.typeId,
        slots: [...res.asked],
        overrides: [...res.overridesUsed].map(([key, m]) => ({
          key, meshId: m.id, name: m.name, role: m.role, stage: m.stage,
          // Honoured, and said: a slot filled with a mesh that classifies as
          // something else. The operator chose it, but the page should not
          // pretend the classifier agreed.
          offRole: key.endsWith('/*') ? m.role !== key.slice(0, -2) : keyOf(m.role, m.stage) !== key,
        })),
        staleOverrides: [...res.staleOverrides],
        conflicts: [...res.conflicts.keys()],
        built: doc.pages.length - pagesBefore,
      })
    }
  }

  const out = { doc, notes, conflicts: [...conflictsByKey.values()], sceneParts }
  if (pageMode) out.pages = perPage
  return out
}
